import { breakpoints } from '@/themes/themeOverrides/Breakpoints';
import { Palette } from '@/themes/themeOverrides/Palette';

const rowOutlineColor = Palette.gray.gray300;
const rowHoverBg = Palette.gray.gray50;
const rowSeparatorShadow = `0 -2px 0 0 ${Palette.gray.gray75}, 0 2px 0 0 ${Palette.gray.gray75} !important`;
const rowOutlineShadow = {
    middle: `1px 0 0 0 ${rowHoverBg}, -1px 0 0 0 ${rowHoverBg}, 0 -2px 0 0 ${rowOutlineColor}, 0 2px 0 0 ${rowOutlineColor}`,
    start: `1px 0 0 0 ${rowHoverBg}, -2px 0 0 0 ${rowOutlineColor}, 0 -2px 0 0 ${rowOutlineColor}, 0 2px 0 0 ${rowOutlineColor}`,
    end: `-1px 0 0 0 ${rowHoverBg}, 2px 0 0 0 ${rowOutlineColor}, 0 -2px 0 0 ${rowOutlineColor}, 0 2px 0 0 ${rowOutlineColor}`,
    full: `0 0 0 2px ${rowOutlineColor}`,
};

export const RaDatagridStyles = {
    styleOverrides: {
        root: {
            color: Palette.black,
            '& .RaDatagrid-tableWrapper': {
                overflowX: 'auto',
                overflowY: 'hidden',
                maxWidth: 'calc(100vw - 360px)',
                minWidth: '100%',
                boxSizing: 'border-box',
                overflowScrolling: 'touch',
                width: '100%',
                borderRadius: '16px 16px 0 0',
                paddingLeft: '2px',
                paddingRight: '2px',
                paddingBottom: '2px',
                marginLeft: '-2px',
                marginRight: '-2px',
                marginBottom: '-2px',
                '& .RaDatagrid-table': {
                    borderCollapse: 'separate',
                    borderSpacing: '0 2px',
                },
                '& .MuiStack-root': {
                    margin: '0px !important',
                    gap: '8px',
                    position: 'relative',
                    top: '2px',
                },
            },
            '& .RaDatagrid-row': {
                boxShadow: rowSeparatorShadow,
                '&:hover': {
                    backgroundColor: `${Palette.gray.gray50} !important`,
                    boxShadow: 'none !important',
                    '& .MuiTableCell-root': {
                        boxShadow: rowOutlineShadow.middle,
                        backgroundColor: `${rowHoverBg} !important`,
                        borderLeft: 'none',
                        borderRight: 'none',
                    },
                    '& .MuiTableCell-root:first-of-type': {
                        boxShadow: rowOutlineShadow.start,
                    },
                    '& .MuiTableCell-root:last-of-type': {
                        boxShadow: rowOutlineShadow.end,
                        '&::after': {
                            display: 'none',
                        },
                    },
                    '& .MuiTableCell-root:first-of-type:last-of-type': {
                        boxShadow: rowOutlineShadow.full,
                    },
                },
                '& .MuiTableCell-root:first-of-type:not(.RaDatagrid-expandIconCell), & .RaDatagrid-expandIconCell + .MuiTableCell-root': {
                    borderTopLeftRadius: '8px',
                    borderBottomLeftRadius: '8px',
                },
                '& .MuiTableCell-root:last-of-type:not(.RaDatagrid-expandIconCell)': {
                    borderTopRightRadius: '8px',
                    borderBottomRightRadius: '8px',
                },
            },
            '& .RaDatagrid-tbody': {
                '& .RaDatagrid-row': {
                    '&:hover, &:active, &.checked': {
                        boxShadow: 'none !important',
                        position: 'relative',
                        zIndex: '3',
                        [`@media (max-width: ${breakpoints.values.md}px)`]: {
                            zIndex: '5',
                        },
                        '& .MuiTableCell-root': {
                            boxShadow: rowOutlineShadow.middle,
                            backgroundColor: rowHoverBg,
                            borderLeft: 'none',
                            borderRight: 'none',
                        },
                        '& .MuiTableCell-root:first-of-type': {
                            boxShadow: rowOutlineShadow.start,
                        },
                        '& .MuiTableCell-root:last-of-type': {
                            boxShadow: rowOutlineShadow.end,
                            '&::after': {
                                display: 'none',
                            },
                        },
                        '& .MuiTableCell-root:first-of-type:last-of-type': {
                            boxShadow: rowOutlineShadow.full,
                        },
                    },
                    '& .MuiTableCell-root': {
                        backgroundColor: Palette.white,
                    },
                },
            },
            '& .RaDatagrid-row:last-child': {
                '& .MuiTableCell-body': {
                    borderBottom: 'none',
                },
            },
            '& .RaDatagrid-headerRow': {
                '&:hover': {
                    backgroundColor: `${Palette.gray.gray100} !important`,
                    '& .MuiTableCell-root': {
                        boxShadow: 'none !important',
                        border: 'none !important',
                        backgroundColor: `${Palette.gray.gray100} !important`,
                    },
                },
            },
            '& .RaDatagrid-headerCell': {
                backgroundColor: `${Palette.gray.gray100} !important`,
                lineHeight: '16px !important',
                borderBottom: 'none !important',
                borderTop: 'none !important',
                '& *:hover, & *:hover *': {
                    textDecoration: 'none !important',
                    boxShadow: 'none !important',
                    border: 'none !important',
                },
                '& .MuiTableSortLabel-root': {
                    '& .MuiTableSortLabel-icon': {
                        fill: Palette.gray.gray500,
                    },
                },
                '&.column-pictureUrl': {
                    '& .MuiButtonBase-root': {
                        display: 'none',
                    },
                },
                '& .RaDatagridHeaderCell-icon': {
                    backgroundImage: `url("data:image/svg+xml,%3Csvg width='20' height='20' viewBox='0 0 20 20' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M6 12L10 15L14 12' stroke='%23949494' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'/%3E%3Cpath d='M10 5V13' stroke='%23949494' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E%0A")`,
                    backgroundRepeat: 'no-repeat',
                    backgroundPosition: 'center',
                    width: '20px',
                    height: '20px',
                    marginLeft: '10px',
                    '& path': {
                        opacity: 0,
                    },
                },
            },
            '& .MuiTableCell-root': {
                borderLeft: 'none',
                borderWidth: '0px',
                fontSize: '14px',
                lineHeight: '20px',
                padding: '20px 24px',
                fontWeight: '600',
                '& *:hover': {
                    [`@media (max-width: ${breakpoints.values.md}px)`]: {
                        textDecoration: 'none !important',
                    },
                },
                '& .MuiTypography-alignRight': {
                    textAlign: 'right',
                    maxWidth: '100%',
                },
                '& .MuiButton-text': {
                    padding: '0 !important',
                    '&:hover': {
                        backgroundColor: 'transparent !important',
                        boxShadow: 'none !important',
                        textDecoration: 'underline !important',
                        color: `${Palette.black} !important`,
                        [`@media (max-width: ${breakpoints.values.md}px)`]: {
                            textDecoration: 'none !important',
                        },
                    },
                },
                '& .gray': {
                    opacity: '0.3',
                },
                '&:first-of-type': {
                    borderLeft: 'none',
                },
                '&.MuiTableCell-paddingCheckbox': {
                    padding: '0px 8px 6px 15px',
                },
                '& .RaDatagrid-expandIconCell, & .MuiTableCell-paddingCheckbox': {
                    backgroundColor: Palette.white,
                },
                '& .MuiTypography-root': {
                    fontSize: '14px',
                    lineHeight: '20px',
                    display: 'block',
                    width: 'auto',
                },
                '& > span': {
                    color: Palette.black,
                    [`@media (min-width: ${breakpoints.values.lg}px)`]: {
                        maxWidth: 'max-content',
                    },
                },
                '&.column-pictureUrl': {
                    textAlign: 'center',
                    padding: '18px 10px 18px 10px',
                    '& .MuiBox-root': {
                        width: 'auto',
                        '& .RaImageField-image': {
                            width: 'auto',
                            height: '60px',
                            margin: 0,
                        },
                    },
                },
                '&.column-volume': {
                    '& .MuiTypography-root': {
                        fontWeight: 700,
                    },
                },
                '&.column-serialNumber': {
                    '& .MuiTypography-root': {
                        whiteSpace: 'nowrap',
                        display: 'block',
                    },
                },
                '&.column-device\\.serialNumber': {
                    '& .MuiTypography-root': {
                        whiteSpace: 'nowrap !important',
                        display: 'block',
                    },
                },
                '&.column-softwareSoc': {
                    '& .MuiBox-root > div + div': {
                        marginLeft: '18px',
                    },
                    '& .MuiBox-root > div': {
                        padding: '0',
                        margin: '-6px 0 0 0',
                    },
                    '& p.MuiTypography-root': {
                        fontSize: '11px',
                    },
                    '& span.MuiTypography-root': {
                        fontSize: '14px',
                        fontWeight: '600',
                        position: 'relative',
                        top: '-4px',
                    },
                },
                '&.column-transactionCount, &.column-containerCount, &.column-deviceCount, &.column-newDeviceCount, &.column-collectedContainerCount': {
                    '& .MuiTypography-root': {
                        fontWeight: 600,
                    },
                },
                '&.column-createdAt, &.column-updatedAt, &.column-deviceCount': {
                    whiteSpace: 'nowrap',
                },
                '&.column-deviceCount': {
                    textAlign: 'left',
                },
                '&.column-tagIds, &.column-tags': {
                    maxWidth: '200px',
                    '& .tag-list': {
                        '& .MuiStack-root': {
                            gap: '4px',
                        },
                        '& .MuiLink-root': {
                            display: 'block',
                            fontSize: '11px',
                            lineHeight: '16px',
                            '& span': {
                                fontSize: '11px',
                                fontWeight: '400',
                                lineHeight: '16px',
                                padding: '0px 8px',
                                color: Palette.black,
                                borderRadius: '4px',
                            },
                        },
                    },
                },
                '&.column-solutionTip': {
                    '& span.MuiTypography-root': {
                        display: 'block',
                        lineHeight: 0,
                    },
                },
                '& a:hover': {
                    textDecoration: 'underline !important',
                    [`@media (max-width: ${breakpoints.values.md}px)`]: {
                        textDecoration: 'none !important',
                    },
                },
                '&:not(.MuiTableCell-head) span[role="button"]:hover, &:not(.MuiTableCell-head) div[role="button"]:hover': {
                    textDecoration: 'underline !important',
                    [`@media (max-width: ${breakpoints.values.md}px)`]: {
                        textDecoration: 'none !important',
                    },
                },
                '& .list-group:hover': {
                    textDecoration: 'underline !important',
                    [`@media (max-width: ${breakpoints.values.md}px)`]: {
                        textDecoration: 'none !important',
                    },
                },
            },
            '&.tags-layout': {
                marginTop: '-14px',
                '& .RaDatagrid-thead': {
                    display: 'none',
                },
                '& tr.RaDatagrid-row': {
                    display: 'grid',
                    gridTemplateRows: 'repeat(2, auto)',
                    gridTemplateColumns: 'repeat(2, 1fr)',
                    gridAutoFlow: 'column',
                    gap: '0px',
                    width: '100%',
                    borderRadius: '20px',
                    marginBottom: '2px',
                    position: 'relative',
                    overflow: 'hidden',
                    cursor: 'default !important',
                    backgroundColor: `${Palette.white} !important`,
                    boxShadow: 'none !important',
                    '&:hover .MuiTableCell-root': {
                        boxShadow: 'none !important',
                        border: 'none !important',
                        backgroundColor: `${Palette.white} !important`,
                    },
                    [`@media (max-width: ${breakpoints.values.lg}px)`]: {
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '0px',
                    },
                    '& td.MuiTableCell-body': {
                        display: 'block',
                        border: 'none',
                        padding: '32px',
                        minWidth: 0,
                        '& span.tag-header': {
                            color: Palette.black,
                            display: 'block',
                            fontSize: '20px',
                            lineHeight: '26px',
                            letterSpacing: '-0.4px',
                            marginBottom: '-12px',
                            fontFamily: 'Rubik, Arial, sans-serif',
                            fontWeight: '500',
                        },
                        '&:last-of-type': {
                            padding: 0,
                            right: '35px',
                            top: '36px',
                            position: 'absolute',
                            [`@media (max-width: ${breakpoints.values.md}px)`]: {
                                top: '30px',
                            },
                        },
                        '&:nth-of-type(3), &:nth-of-type(4)': {
                            '& > span, & > .MuiStack-root': {
                                position: 'relative',
                                left: '-54px',
                                [`@media (max-width: ${breakpoints.values.lg}px)`]: {
                                    left: '0',
                                },
                            },
                        },
                        '&.column-tagIds, &.column-tags': {
                            padding: '0 32px 32px 32px',
                            minHeight: '38px',
                            width: '100%',
                            minWidth: '100%',
                            '& .tag-list': {
                                maxWidth: '90%',
                                minHeight: '38px',
                                '& > span': {
                                    display: 'block',
                                    minHeight: '38px',
                                },
                                '& .MuiStack-root': {
                                    margin: 0,
                                    gap: '8px',
                                },
                                '& .MuiTypography-root': {
                                    '& span': {
                                        display: 'block',
                                        fontSize: '14px',
                                        lineHeight: '20px',
                                        padding: '8px 16px',
                                        borderRadius: '10px',
                                        backgroundColor: 'gray',
                                        [`@media (max-width: ${breakpoints.values.md}px)`]: {
                                            padding: '4px 8px',
                                            borderRadius: '8px',
                                            fontSize: '12px',
                                            lineHeight: '18px',
                                        },
                                    },
                                },
                            },
                        },
                    },
                },
            },
            '& .MuiButtonBase-root': {
                position: 'relative',
                '& .arrow-icon': {
                    position: 'relative',
                    backgroundImage: `url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="10" height="12" viewBox="0 0 10 12" fill="none"><path d="M1 8L5 11L9 8" stroke="%23B4B4B4" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/><path d="M5 1V9" stroke="%23B4B4B4" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>')`,
                    backgroundRepeat: 'no-repeat',
                    backgroundPosition: 'center',
                    backgroundColor: 'transparent',
                    borderRadius: '4px',
                    width: '20px',
                    height: '20px',
                    marginLeft: '12px !important',
                    transition: 'transform 500ms ease-in-out',
                    '& path': {
                        opacity: 0,
                    },
                },
            },
            '& .MuiButtonBase-root.Mui-active': {
                '& .arrow-icon': {
                    backgroundImage: `url('data:image/svg+xml,<svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg"><g id="icon"><g id="Group 2"><path id="Vector 12" d="M7 11L10 14L13 11" stroke="%231A1A1A" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/><path id="Vector 13" d="M10 6V13" stroke="%231A1A1A" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/></g></g></svg>')`,
                    backgroundColor: Palette.white,
                },
                '& .arrow-icon-rotate': {
                    transform: 'rotate(180deg)',
                    transition: 'transform 0.3s ease-in-out',
                },
            },
            '&.responsive-table': {
                '& .RaDatagrid-tableWrapper': {
                    overflow: 'visible',
                    [`@media (min-width: ${breakpoints.values.lg}px)`]: {
                        overflowX: 'auto',
                        overflowY: 'hidden',
                    },
                },
                '& .RaDatagrid-table': {
                    tableLayout: 'fixed',
                    [`@media (min-width: ${breakpoints.values.lg}px)`]: {
                        tableLayout: 'auto',
                    },
                },
                '& .MuiTableHead-root': {
                    display: 'none',
                    [`@media (min-width: ${breakpoints.values.lg}px)`]: {
                        display: 'table-header-group',
                    },
                },
                '& .MuiTableSortLabel-root': {
                    fontWeight: '600',
                },
                '& .RaDatagrid-tbody': {
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '2px',
                    [`@media (min-width: ${breakpoints.values.lg}px)`]: {
                        display: 'table-row-group',
                        flexDirection: 'row',
                        gap: 0,
                    },
                },
                '& .RaDatagrid-tbody .RaDatagrid-row': {
                    display: 'flex',
                    flexDirection: 'column',
                    padding: '32px',
                    backgroundColor: Palette.white,
                    borderRadius: '8px',
                    width: '100%',
                    margin: 'auto',
                    boxShadow: Palette.boxShadow.elevation2,
                    '&:hover, &:active, &.checked': {
                        backgroundColor: Palette.white,
                        [`@media (max-width: ${breakpoints.values.lg - 1}px)`]: {
                            boxShadow: `${Palette.boxShadow.elevation2}, ${rowOutlineShadow.full}`,
                            '& .MuiTableCell-root': {
                                boxShadow: 'none',
                            },
                        },
                        [`@media (min-width: ${breakpoints.values.lg}px)`]: {
                            backgroundColor: Palette.gray.gray50,
                        },
                    },
                    [`@media (min-width: ${breakpoints.values.lg}px)`]: {
                        display: 'table-row',
                        flexDirection: 'row',
                        padding: 0,
                        width: 'auto',
                        margin: 0,
                        boxShadow: 'none',
                    },
                    '& .MuiTableCell-root': {
                        [`@media (max-width: ${breakpoints.values.md}px)`]: {
                            backgroundColor: Palette.white,
                        },
                    },
                    '& .MuiTableCell-root:first-of-type': {
                        borderRadius: 0,
                        [`@media (min-width: ${breakpoints.values.lg}px)`]: {
                            borderTopLeftRadius: '8px',
                            borderBottomLeftRadius: '8px',
                        },
                    },
                    '& .MuiTableCell-root:last-of-type': {
                        borderRadius: 0,
                        [`@media (min-width: ${breakpoints.values.lg}px)`]: {
                            borderTopRightRadius: '8px',
                            borderBottomRightRadius: '8px',
                        },
                    },
                },
                '& .MuiTableCell-root': {
                    padding: 0,
                    border: 'none',
                    '& .MuiTypography-root': {
                        display: 'block',
                        width: '100%',
                    },
                    [`@media (min-width: ${breakpoints.values.lg}px)`]: {
                        padding: '24px',
                        '& .MuiTypography-root': {
                            display: 'inline',
                            width: 'auto',
                        },
                    },
                },
                '& .chip-small': {
                    borderRadius: '4px',
                    height: '16px',
                },
                '& p': {
                    margin: 0,
                },
                '& .device-status-field': {
                    borderTop: `2px solid ${Palette.gray.gray75}`,
                    padding: '8px 12px',
                    width: '100%',
                    display: 'block',
                    fontSize: '11px',
                    [`@media (min-width: ${breakpoints.values.lg}px)`]: {
                        borderTop: 'none',
                        padding: 'inherit',
                        width: 'auto',
                        fontSize: 'inherit',
                    },
                    '& .device-status-field-time': {
                        fontWeight: 400,
                        [`@media (min-width: ${breakpoints.values.lg}px)`]: {
                            fontWeight: 600,
                        },
                    },
                },
                '& .device-status-field-last': {
                    display: 'flex',
                    alignItems: 'center',
                    borderBottom: `2px solid ${Palette.gray.gray75}`,
                    marginBottom: '20px',
                    flexWrap: 'nowrap',
                    '& > div': {
                        flexDirection: 'row',
                        width: 'max-content',
                    },
                    [`@media (min-width: ${breakpoints.values.lg}px)`]: {
                        borderBottom: 'none',
                        marginBottom: 0,
                        '& > div': {
                            flexDirection: 'column',
                        },
                    },
                },
                '& .service-request-device-status-field': {
                    padding: '20px 0 8px',
                    border: 'none',
                    marginBottom: 0,
                    '& .device-status-field-time': {
                        [`@media (min-width: ${breakpoints.values.lg}px)`]: {
                            fontWeight: 400,
                        },
                    },
                },
                '& .toggle-drawer-button': {
                    width: '36px',
                    height: '36px',
                    marginLeft: 'auto',
                    [`@media (min-width: ${breakpoints.values.lg}px)`]: {
                        display: 'none',
                    },
                },
                '& .MuiTableCell-paddingCheckbox': {
                    display: 'none',
                    [`@media (min-width: ${breakpoints.values.lg}px)`]: {
                        display: 'table-cell',
                    },
                },
                '&.service-list': {
                    '& .device-pos-field': {
                        marginTop: '20px',
                        [`@media (min-width: ${breakpoints.values.lg}px)`]: {
                            marginTop: '0px',
                        },
                        '& .pos-field-name': {
                            marginBottom: '0px',
                            display: 'block',
                            [`@media (min-width: ${breakpoints.values.lg}px)`]: {
                                marginBottom: '4px',
                                display: 'flex',
                            },
                        },
                        '& .pos-field-phone': {
                            display: 'none',
                            [`@media (min-width: ${breakpoints.values.lg}px)`]: {
                                display: 'block',
                            },
                        },
                        '& .pos-field-address': {
                            marginBottom: '0px',
                            [`@media (min-width: ${breakpoints.values.lg}px)`]: {
                                marginBottom: '-4px',
                            },
                        },
                    },
                    '& .service-request-serial-number': {
                        paddingBottom: '20px',
                        borderBottom: `2px solid ${Palette.gray.gray75}`,
                        marginTop: '4px',
                        [`@media (min-width: ${breakpoints.values.lg}px)`]: {
                            marginTop: 0,
                            paddingBottom: 0,
                            borderBottom: 'none',
                        },
                    },
                    '& .device-status-field-last.service-request-device-status-field': {
                        border: 'none',
                        padding: '12px 0 0',
                        marginBottom: 0,
                        [`@media (min-width: ${breakpoints.values.lg}px)`]: {
                            padding: 0,
                        },
                    },
                    '& .unassigned-field': {
                        display: 'block',
                        marginTop: '20px',
                        [`@media (min-width: ${breakpoints.values.lg}px)`]: {
                            marginTop: 0,
                        },
                    },
                },
                '& .column-devices': {
                    '& .MuiListItem-root': {
                        paddingLeft: '0 !important',
                        [`@media (max-width: ${breakpoints.values.lg}px)`]: {
                            padding: '0 !important',
                        },
                        '& .MuiTypography-caption': {
                            [`@media (max-width: ${breakpoints.values.lg}px)`]: {
                                display: 'inline-block',
                                width: 'auto',
                            },
                        },
                    },
                },
            },
        },
    },
};
